import React from 'react';

interface StatusBadgeProps {
  status?: string;
  stage?: string;
  type?: 'order' | 'repair' | 'oldgold';
  karigarDelayed?: boolean;
  className?: string;
}

type BadgeConfig = {
  bg: string;
  text: string;
  label: string;
};

const ORDER_STAGES: Record<string, BadgeConfig> = {
  STAGE_1_PLACEMENT: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Order Placed' },
  STAGE_2_KARIGAR: { bg: 'bg-yellow-400', text: 'text-yellow-900', label: 'With Karigar' },
  STAGE_3_QC: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Ready for QC' },
  STAGE_4_LEDGER: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Ledger Clearance' },
  STAGE_5_DELIVERY: { bg: 'bg-green-500', text: 'text-white', label: 'Delivered' },
  STAGE_5_DELIVERED: { bg: 'bg-green-500', text: 'text-white', label: 'Delivered' },
};

const REPAIR_STAGES: Record<string, BadgeConfig> = {
  INWARD: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Inward' },
  KARIGAR_ROUTING: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'With Karigar' },
  QC_CHECK: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'QC Check' },
  DELIVERY: { bg: 'bg-green-500', text: 'text-white', label: 'Delivered' },
};

const DELAYED: BadgeConfig = { bg: 'bg-red-500', text: 'text-white', label: 'Delayed / Overdue' };

const ORDER_STATUS_MAP: Record<string, BadgeConfig> = {
  pending: { bg: 'bg-slate-200', text: 'text-slate-700', label: 'Pending' },
  approved: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Approved' },
  completed: { bg: 'bg-green-500', text: 'text-white', label: 'Completed' },
  cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled' },
};

const OLDGOLD_STATUS_MAP: Record<string, BadgeConfig> = {
  pending: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Pending' },
  processed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Processed' },
  exchanged: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Exchanged' },
  rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' },
  completed: { bg: 'bg-green-500', text: 'text-white', label: 'Completed' },
};

const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  stage,
  type = 'order',
  karigarDelayed = false,
  className = '',
}) => {
  let config: BadgeConfig | null = null;
  const activeStage = stage || status;

  if (karigarDelayed) {
    config = DELAYED;
  } else if (type === 'order' && activeStage && ORDER_STAGES[activeStage]) {
    config = ORDER_STAGES[activeStage];
  } else if (type === 'repair' && activeStage && REPAIR_STAGES[activeStage]) {
    config = REPAIR_STAGES[activeStage];
  } else if (type === 'order' && activeStage && ORDER_STATUS_MAP[activeStage]) {
    config = ORDER_STATUS_MAP[activeStage];
  } else if (type === 'oldgold' && activeStage && OLDGOLD_STATUS_MAP[activeStage]) {
    config = OLDGOLD_STATUS_MAP[activeStage];
  } else {
    config = { bg: 'bg-slate-100', text: 'text-slate-600', label: activeStage || 'Unknown' };
  }

  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ${config.bg} ${config.text} ${className}`}
    >
      {config.label}
    </span>
  );
};

export default StatusBadge;
